(function () {
  const root = document.getElementById("burst-chat-app");
  if (!root) return;

  const API_BASE = (
    root.dataset.apiBase || "https://conversation-ai-dev.burstoralcare.com"
  ).replace(/\/+$/, "");
  const API_TOKEN = root.dataset.apiToken || "";
  const USERS_API = root.dataset.usersApi || "/v1/users";

  // Every call takes 60-90s: the model reads the full product catalogue on each turn.
  const REQUEST_TIMEOUT_MS = 180000;

  const STORAGE_ID = "burst_conversation_id";
  const STORAGE_USER = "burst_conversation_user";
  const GENERIC_ERROR =
    "Something went wrong on our end. Please try again in a moment.";

  const userSelect = document.getElementById("chat-user");
  const inputEl = document.getElementById("chat-input");
  const sendBtn = document.getElementById("send-message");
  const messageList = document.getElementById("message-list");
  const resetBtn = document.getElementById("reset-chat");
  const typingIndicator = document.getElementById("typing-indicator");
  const elapsedEl = document.getElementById("typing-elapsed");
  const chatBody = document.getElementById("chat-body");
  const inspectTitleEl = document.getElementById("inspect-title");
  const inspectBoxEl = document.getElementById("inspect-box");

  let conversationId = null;
  let inFlight = false;
  let closed = false;
  let elapsedTimer = null;

  function getTimestamp() {
    return new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  function scrollToBottom() {
    chatBody.scrollTop = chatBody.scrollHeight;
  }

  function appendMessage(role, content, timestamp) {
    const row = document.createElement("div");
    row.className = `message-row ${role}`;

    const meta = document.createElement("div");
    meta.className = "message-meta";

    const sender = document.createElement("span");
    sender.className = "sender-label";
    sender.textContent = role === "bot" ? "BURST AI" : "You";

    const time = document.createElement("span");
    time.className = "timestamp-text";
    time.textContent = timestamp || getTimestamp();

    meta.appendChild(sender);
    meta.appendChild(time);

    const bubble = document.createElement("div");
    bubble.className = `message-bubble ${role}`;
    bubble.textContent = content;

    row.appendChild(meta);
    row.appendChild(bubble);
    messageList.appendChild(row);
    scrollToBottom();
  }

  function appendSystemNote(text, variant) {
    const note = document.createElement("div");
    note.className = `system-note${variant ? " " + variant : ""}`;
    note.textContent = text;
    messageList.appendChild(note);
    scrollToBottom();
  }

  function showTyping(show) {
    typingIndicator.style.display = show ? "block" : "none";
    clearInterval(elapsedTimer);

    if (!show) {
      elapsedEl.textContent = "";
      return;
    }

    // 60-90s of silence reads as a frozen app, so surface a running counter.
    const startedAt = Date.now();
    elapsedEl.textContent = "0s";
    elapsedTimer = setInterval(() => {
      elapsedEl.textContent = `${Math.round((Date.now() - startedAt) / 1000)}s`;
    }, 1000);
    scrollToBottom();
  }

  function setBusy(busy) {
    inFlight = busy;
    const lock = busy || closed || !conversationId;
    inputEl.disabled = lock;
    sendBtn.disabled = lock;
    userSelect.disabled = busy;
    showTyping(busy);
  }

  function markClosed(reason) {
    closed = true;
    inputEl.disabled = true;
    sendBtn.disabled = true;
    inputEl.value = "";
    inputEl.placeholder = "Conversation ended — reset to start a new one";
    appendSystemNote(
      reason ? `Conversation ended: ${reason}` : "Conversation ended.",
      "closed",
    );
  }

  function errorText(status, payload) {
    // error.message is safe to surface except on 500.
    if (status === 500) return GENERIC_ERROR;

    const error = payload && payload.error;
    if (!error || !error.message) return GENERIC_ERROR;

    // On 400 the useful text is in details — message is just "Request validation failed".
    const details = Array.isArray(error.details)
      ? error.details
          .map((d) => d.message)
          .filter(Boolean)
          .join("; ")
      : "";

    return details ? `${error.message}: ${details}` : error.message;
  }

  async function request(path, options) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      const res = await fetch(`${API_BASE}${path}`, {
        ...options,
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
          ...(API_TOKEN ? { Authorization: `Bearer ${API_TOKEN}` } : {}),
          ...(options && options.headers),
        },
      });

      let data = null;
      try {
        data = await res.json();
      } catch (err) {
        data = null;
      }

      return { ok: res.ok, status: res.status, data };
    } finally {
      clearTimeout(timer);
    }
  }

  const INSPECT_TITLE = "Inspect";
  const INSPECT_EMPTY = "Nothing to inspect yet.";

  // Guardrail outcome -> row status, per the integration notes.
  const GUARDRAIL_STATUS = {
    passed: "ok",
    passed_after_rewrite: "ok",
    blocked: "blocked",
    would_block: "blocked",
    unscreened: "warn",
    skipped: "skipped",
    not_applicable: "skipped",
  };

  const GUARDRAIL_TEXT = {
    passed: "Passed first try.",
    passed_after_rewrite:
      "Passed after a rewrite — the first reply was rejected.",
    blocked: "Blocked. The customer received a pre-approved handoff line.",
    would_block:
      "Shadow mode: this would have been blocked, but it was still sent.",
    unscreened: "Unscreened — the judge was unreachable.",
    skipped: "Guardrail is switched off.",
    not_applicable: "There was no reply to screen.",
  };

  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = String(text);
    return node;
  }

  function inspectSection(title) {
    const wrap = el("section", "inspect-section");
    if (title) wrap.appendChild(el("h4", "inspect-heading", title));
    return wrap;
  }

  function stepRow(step) {
    const row = el("div", `inspect-step is-${step.status || "info"}`);

    const head = el("div", "inspect-step-head");
    head.appendChild(el("span", "inspect-dot"));
    head.appendChild(el("span", "inspect-step-label", step.label || step.id));
    row.appendChild(head);

    if (step.detail)
      row.appendChild(el("p", "inspect-step-detail", step.detail));

    // evidence is raw facts, never prose — capped at 2 by the backend.
    if (Array.isArray(step.evidence)) {
      step.evidence
        .slice(0, 2)
        .forEach((line) =>
          row.appendChild(el("code", "inspect-evidence", line)),
        );
    }

    const meta = [
      step.source,
      step.durationMs != null ? `${step.durationMs}ms` : null,
    ]
      .filter(Boolean)
      .join(" · ");
    if (meta) row.appendChild(el("span", "inspect-step-meta", meta));

    return row;
  }

  function clearInspect() {
    if (!inspectBoxEl) return;
    inspectTitleEl.textContent = INSPECT_TITLE;
    inspectBoxEl.innerHTML = "";
    inspectBoxEl.appendChild(el("span", "inspect-empty", INSPECT_EMPTY));
  }

  function renderHeader(box, inspector) {
    const outcome = inspector.outcome || {};
    const head = el("div", "inspect-badges");

    if (outcome.decision)
      head.appendChild(el("span", "inspect-chip", outcome.decision));
    if (outcome.safetyTier)
      head.appendChild(el("span", "inspect-chip", outcome.safetyTier));
    if (outcome.intent)
      head.appendChild(el("span", "inspect-chip", outcome.intent));
    box.appendChild(head);

    const bits = [];
    if (inspector.turnNumber != null) bits.push(`turn ${inspector.turnNumber}`);
    if (inspector.durationMs != null)
      bits.push(`${(inspector.durationMs / 1000).toFixed(1)}s`);
    if (inspector.model && inspector.model.name)
      bits.push(inspector.model.name);
    if (bits.length)
      box.appendChild(el("div", "inspect-subhead", bits.join(" · ")));

    // A simulated Recharge write must not read as a real one.
    if (outcome.subscriptionAdded && outcome.subscriptionAdded.simulated) {
      box.appendChild(
        el(
          "div",
          "inspect-flag is-warn",
          "Subscription add was SIMULATED — no real Recharge write.",
        ),
      );
    }
  }

  function renderGuardrail(box, inspector) {
    const g = (inspector.outcome || {}).guardrail;
    if (!g) return;

    const wrap = inspectSection("Guardrail");
    const status = GUARDRAIL_STATUS[g.outcome] || "info";

    const badge = el("div", `inspect-flag is-${status}`);
    badge.appendChild(el("strong", null, g.outcome));
    if (g.mode)
      badge.appendChild(el("span", "inspect-step-meta", ` mode: ${g.mode}`));
    wrap.appendChild(badge);

    if (GUARDRAIL_TEXT[g.outcome])
      wrap.appendChild(
        el("p", "inspect-step-detail", GUARDRAIL_TEXT[g.outcome]),
      );

    if (Array.isArray(g.breachedCategories) && g.breachedCategories.length) {
      wrap.appendChild(
        el(
          "code",
          "inspect-evidence",
          `breached: ${g.breachedCategories.join(", ")}`,
        ),
      );
    }

    const bits = [];
    if (g.attempts != null) bits.push(`${g.attempts} attempt(s)`);
    if (g.rewritten) bits.push("rewritten");
    if (g.handedOff) bits.push("handed off");
    if (g.totalLatencyMs != null) bits.push(`${g.totalLatencyMs}ms`);
    if (bits.length)
      wrap.appendChild(el("span", "inspect-step-meta", bits.join(" · ")));

    box.appendChild(wrap);
  }

  function renderProfileAndEligibility(box, steps) {
    const profile = steps.find((s) => s.id === "intake.profile");
    if (profile) {
      const wrap = inspectSection("Customer");
      wrap.appendChild(stepRow(profile));
      box.appendChild(wrap);
    }

    // Eligibility runs once, at conversation start. Later turns carry only the
    // intake request step, which is not a gate and must not be labelled as one.
    const gates = steps.filter(
      (s) => s.id && s.id.indexOf("eligibility.") === 0,
    );
    if (gates.length) {
      const wrap = inspectSection("Eligibility");
      gates.forEach((s) => wrap.appendChild(stepRow(s)));
      box.appendChild(wrap);
    }
  }

  function renderKnowledge(box, inspector) {
    const versions = inspector.contextVersions;
    if (!versions || typeof versions !== "object") return;

    const keys = Object.keys(versions);
    if (!keys.length) return;

    const wrap = inspectSection("Knowledge loaded");
    const chips = el("div", "inspect-chips");
    keys.forEach((k) => {
      const chip = el("span", "inspect-chip", k);
      chip.title = String(versions[k]);
      chips.appendChild(chip);
    });
    wrap.appendChild(chips);
    box.appendChild(wrap);
  }

  function renderFunnel(box, inspector, steps) {
    const funnel = inspector.funnel;
    if (!funnel) return;

    const wrap = inspectSection("Recommendation");

    const counts = [];
    if (funnel.evaluated != null) counts.push(`${funnel.evaluated} evaluated`);
    if (funnel.eliminated != null)
      counts.push(`${funnel.eliminated} eliminated`);
    if (funnel.candidates != null)
      counts.push(`${funnel.candidates} candidates`);
    if (counts.length)
      wrap.appendChild(el("div", "inspect-step-meta", counts.join(" → ")));

    // categories arrive pre-ordered: selected, then eligible, then suppressed.
    (funnel.categories || []).forEach((cat) => {
      const row = el("div", `inspect-cat is-${cat.status}`);
      const top = el("div", "inspect-cat-head");
      top.appendChild(el("span", "inspect-cat-name", cat.category));
      top.appendChild(el("span", `inspect-chip ${cat.status}`, cat.status));
      row.appendChild(top);

      if (cat.reason)
        row.appendChild(el("p", "inspect-step-detail", cat.reason));

      const nums = [];
      if (cat.products != null) nums.push(`${cat.products} products`);
      if (cat.surviving != null) nums.push(`${cat.surviving} surviving`);
      if (cat.offeredHere) nums.push("offered from here");
      if (nums.length)
        row.appendChild(el("span", "inspect-step-meta", nums.join(" · ")));

      wrap.appendChild(row);
    });

    box.appendChild(wrap);

    // An offer the gates ruled out is a rule violation that must stay visible.
    const outsideGates = steps.find(
      (s) => s.id === "decide.offer_outside_gates",
    );
    if (outsideGates) {
      box.appendChild(
        el(
          "div",
          "inspect-flag is-warn",
          outsideGates.label || "Offer went out against an applied gate.",
        ),
      );
    }
  }

  function renderProduct(box, inspector, steps) {
    const product = (inspector.outcome || {}).product;
    const conclusion = steps.find((s) => s.id === "decide.conclusion");
    const reasoning = steps.find((s) => s.id === "decide.reasoning");
    if (!product && !conclusion && !reasoning) return;

    const wrap = inspectSection("Why this pick");

    if (product) {
      const card = el("div", "inspect-product");
      card.appendChild(el("span", "inspect-product-title", product.title));
      const sub = [
        product.variantTitle,
        product.price ? `$${product.price}` : null,
      ]
        .filter(Boolean)
        .join(" · ");
      if (sub) card.appendChild(el("span", "inspect-step-meta", sub));
      wrap.appendChild(card);
    }

    if (conclusion && conclusion.detail)
      wrap.appendChild(el("p", "inspect-step-detail", conclusion.detail));
    if (reasoning && reasoning.detail)
      wrap.appendChild(el("blockquote", "inspect-quote", reasoning.detail));

    box.appendChild(wrap);
  }

  function renderTrace(box, steps) {
    if (!steps.length) return;

    const wrap = el("details", "inspect-trace");
    wrap.appendChild(
      el("summary", "inspect-heading", `Full trace (${steps.length} steps)`),
    );

    // Steps without `attempt` belong to the turn, not to one attempt.
    let currentAttempt;
    steps.forEach((step) => {
      if (step.attempt !== currentAttempt) {
        currentAttempt = step.attempt;
        if (currentAttempt !== undefined) {
          wrap.appendChild(
            el("div", "inspect-attempt", `Attempt ${currentAttempt}`),
          );
        }
      }
      wrap.appendChild(stepRow(step));
    });

    box.appendChild(wrap);
  }

  function renderFooter(box, inspector, meta) {
    const bits = [];
    const counts = inspector.counts || {};
    ["warn", "failed", "blocked"].forEach((k) => {
      if (counts[k]) bits.push(`${counts[k]} ${k}`);
    });
    if (meta && meta.cost && meta.cost.totalUsd != null)
      bits.push(`$${Number(meta.cost.totalUsd).toFixed(4)}`);
    if (meta && meta.latencyMs != null)
      bits.push(`${(meta.latencyMs / 1000).toFixed(1)}s`);
    if (bits.length)
      box.appendChild(el("div", "inspect-footer", bits.join(" · ")));
  }

  // One block per reply, stacked oldest-first. Never replaces what is already there —
  // only resetInspect() clears, and that happens with the chat.
  function appendInspection(inspector, meta) {
    if (!inspectBoxEl || !inspector) return;

    const empty = inspectBoxEl.querySelector(".inspect-empty");
    if (empty) empty.remove();

    const block = el("article", "inspect-turn");
    const outcome = inspector.outcome || {};

    const head = el("div", "inspect-turn-head");
    const turnLabel =
      inspector.turnNumber != null ? `Turn ${inspector.turnNumber}` : "Turn";
    head.appendChild(el("span", "inspect-turn-number", turnLabel));
    if (outcome.messageType)
      head.appendChild(
        el("span", "inspect-chip decision", outcome.messageType),
      );
    block.appendChild(head);

    // The trace never fails the reply — render the note, keep the block.
    if (inspector.error && inspector.error.code === "INSPECTOR_UNAVAILABLE") {
      block.appendChild(
        el(
          "div",
          "inspect-flag is-warn",
          "Trace unavailable for this turn. The reply itself is unaffected.",
        ),
      );
      inspectBoxEl.appendChild(block);
      inspectBoxEl.scrollTop = inspectBoxEl.scrollHeight;
      return;
    }

    renderHeader(block, inspector);

    if (inspector.trimmed) {
      block.appendChild(
        el(
          "div",
          "inspect-flag is-skipped",
          "Detail trimmed — this turn is old enough that its steps were dropped.",
        ),
      );
    }

    const steps = Array.isArray(inspector.steps) ? inspector.steps : [];

    renderGuardrail(block, inspector);
    renderProfileAndEligibility(block, steps);
    renderKnowledge(block, inspector);
    renderFunnel(block, inspector, steps);
    renderProduct(block, inspector, steps);
    renderTrace(block, steps);
    renderFooter(block, inspector, meta);

    inspectBoxEl.appendChild(block);
    inspectBoxEl.scrollTop = inspectBoxEl.scrollHeight;
  }

  function renderPayload(data) {
    const reply = data && data.reply;

    if (!reply) {
      appendSystemNote(GENERIC_ERROR, "error");
    } else if (reply.action === "SEND") {
      if (reply.message) appendMessage("bot", reply.message, getTimestamp());
    } else if (reply.action === "ESCALATE") {
      appendSystemNote(
        "Handed to a human — someone from the team will follow up.",
      );
    }
    // NO_SEND: intentionally renders nothing.

    if (data) appendInspection(data.inspector, data.meta);

    if (data && data.status === "closed") markClosed(data.closedReason);
  }

  function extractHistory(data) {
    if (!data) return [];

    // GET /v1/conversations/:id returns decisions[], oldest first. Each entry holds
    // that turn's inbound text (on the trace) and the assistant's output.
    if (Array.isArray(data.decisions)) {
      const out = [];
      data.decisions.forEach((d) => {
        const inbound =
          d.inspector && d.inspector.trigger && d.inspector.trigger.inboundText;
        if (inbound) out.push({ role: "user", text: inbound });
        const reply = d.output && d.output.message;
        if (reply) out.push({ role: "bot", text: reply });
      });
      return out;
    }

    const items = data.messages || data.transcript || data.turns || [];
    if (!Array.isArray(items)) return [];

    return items
      .map((item) => {
        const role = item.role || item.sender || item.author;
        const text =
          item.text ||
          item.message ||
          item.content ||
          (item.reply && item.reply.message);
        if (!text) return null;
        return {
          role: role === "assistant" || role === "bot" ? "bot" : "user",
          text,
        };
      })
      .filter(Boolean);
  }

  // A timeout does not mean the turn failed — the server may have finished and only
  // the response was slow. Never retry the POST; read the real state instead.
  async function reconcile() {
    if (!conversationId) return;

    try {
      const { ok, data } = await request(
        `/v1/conversations/${conversationId}`,
        { method: "GET" },
      );
      if (!ok) {
        appendSystemNote(
          "Still working on that reply. Reset if it doesn't arrive shortly.",
          "error",
        );
        return;
      }

      const history = extractHistory(data);
      const last = history[history.length - 1];
      if (last && last.role === "bot") {
        appendMessage("bot", last.text, getTimestamp());
      } else {
        appendSystemNote(
          "Still working on that reply. Reset if it doesn't arrive shortly.",
          "error",
        );
      }

      if (data) appendInspection(data.inspector, data.meta);
      if (data && data.status === "closed") markClosed(data.closedReason);
    } catch (err) {
      console.error("Reconcile error", err);
      appendSystemNote(
        "Couldn't confirm the last reply. Reset to start over.",
        "error",
      );
    }
  }

  async function startConversation(user) {
    setBusy(true);

    // Email is the documented identifier; profileId is only used when there is no email.
    const body = user.email
      ? { email: user.email }
      : { profileId: user.profileId };

    try {
      const { ok, status, data } = await request("/v1/conversations", {
        method: "POST",
        body: JSON.stringify(body),
      });

      if (!ok) {
        appendSystemNote(errorText(status, data), "error");
        return;
      }

      conversationId = data.conversationId;
      localStorage.setItem(STORAGE_ID, conversationId);
      renderPayload(data);
    } catch (err) {
      if (err.name === "AbortError") {
        appendSystemNote(
          "That took too long to start. Please try again.",
          "error",
        );
      } else {
        console.error("Start error", err);
        appendSystemNote(GENERIC_ERROR, "error");
      }
    } finally {
      setBusy(false);
    }
  }

  async function sendMessage() {
    if (inFlight || closed || !conversationId) return;

    const text = inputEl.value.trim();
    if (!text) return;

    inputEl.value = "";
    appendMessage("user", text, getTimestamp());
    setBusy(true);

    try {
      const { ok, status, data } = await request(
        `/v1/conversations/${conversationId}/messages`,
        {
          method: "POST",
          body: JSON.stringify({ text }),
        },
      );

      if (!ok) {
        if (status === 409) {
          markClosed(
            (data && data.error && data.error.message) || "already closed",
          );
          return;
        }
        appendSystemNote(errorText(status, data), "error");
        return;
      }

      renderPayload(data);
    } catch (err) {
      if (err.name === "AbortError") {
        await reconcile();
      } else {
        console.error("Send error", err);
        appendSystemNote(GENERIC_ERROR, "error");
      }
    } finally {
      setBusy(false);
    }
  }

  async function restore() {
    const storedId = localStorage.getItem(STORAGE_ID);
    if (!storedId) return false;

    try {
      const { ok, data } = await request(`/v1/conversations/${storedId}`, {
        method: "GET",
      });
      if (!ok) {
        localStorage.removeItem(STORAGE_ID);
        return false;
      }

      conversationId = storedId;
      extractHistory(data).forEach((msg) =>
        appendMessage(msg.role, msg.text, getTimestamp()),
      );

      // Restore every turn's trace, oldest first — the panel is a running log.
      (data.decisions || []).forEach((d) => appendInspection(d.inspector, d));

      if (data && data.status === "closed") {
        markClosed(data.closedReason);
      } else {
        inputEl.disabled = false;
        sendBtn.disabled = false;
      }
      return true;
    } catch (err) {
      console.error("Restore error", err);
      localStorage.removeItem(STORAGE_ID);
      return false;
    }
  }

  // Clears the transcript and forgets the conversation, without touching the customer list.
  function resetChat() {
    localStorage.removeItem(STORAGE_ID);
    localStorage.removeItem(STORAGE_USER);
    conversationId = null;
    closed = false;
    messageList.innerHTML = "";
    inputEl.value = "";
    inputEl.placeholder = "Watcha lookin’ for?";
    inputEl.disabled = true;
    sendBtn.disabled = true;
    showTyping(false);
    clearInspect();
  }

  sendBtn.addEventListener("click", () => sendMessage());
  inputEl.addEventListener("keydown", (e) => {
    if (e.key === "Enter") sendMessage();
  });

  resetBtn.addEventListener("click", () => {
    resetChat();
    userSelect.value = "";
  });

  let users = [];

  function userKey(user) {
    return user.profileId || user.email;
  }

  function normalizeUsers(data) {
    const list = Array.isArray(data)
      ? data
      : data.users || data.customers || data.results || [];
    if (!Array.isArray(list)) return [];

    return list
      .map((u) => {
        const email = u.email || u.emailAddress || u.email_address;
        const profileId =
          u.klaviyoId || u.profileId || u.profile_id || u.klaviyoProfileId;
        if (!email && !profileId) return null;

        const name =
          u.name ||
          u.fullName ||
          [u.firstName || u.first_name, u.lastName || u.last_name]
            .filter(Boolean)
            .join(" ");

        return {
          email,
          profileId,
          label: name ? `${name} (${email || profileId})` : email || profileId,
        };
      })
      .filter(Boolean);
  }

  async function loadUsers() {
    try {
      // A path goes through request() so it inherits the base URL, token and timeout.
      const { ok, status, data } = USERS_API.startsWith("/")
        ? await request(USERS_API, { method: "GET" })
        : await fetch(USERS_API, {
            headers: API_TOKEN ? { Authorization: `Bearer ${API_TOKEN}` } : {},
          }).then(async (r) => ({
            ok: r.ok,
            status: r.status,
            data: await r.json(),
          }));

      if (!ok) throw new Error(`HTTP ${status}`);

      users = normalizeUsers(data);

      if (!users.length) {
        appendSystemNote("The users API returned no customers.", "error");
        return;
      }

      users.forEach((u, i) => {
        const opt = document.createElement("option");
        opt.value = String(i);
        opt.textContent = u.label;
        userSelect.appendChild(opt);
      });
    } catch (err) {
      console.error("Users load error", err);
      appendSystemNote("Couldn't load the customer list.", "error");
    }
  }

  // Switching customer always wipes the chat — a conversation belongs to one person
  // and is not resumable for another.
  userSelect.addEventListener("change", () => {
    resetChat();

    const user = users[Number(userSelect.value)];
    if (!user) return;

    localStorage.setItem(STORAGE_USER, userKey(user));
    startConversation(user);
  });

  async function init() {
    await loadUsers();
    if (!(await restore())) return;

    // Re-select whoever the restored conversation belongs to.
    const key = localStorage.getItem(STORAGE_USER);
    const index = users.findIndex((u) => userKey(u) === key);
    if (index >= 0) userSelect.value = String(index);
  }

  init();
})();
